import { Switch as HeadSwitch } from '@headlessui/react';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import type { FormItemProps } from '../../blocks/FormItem';
import FormItem from '../../blocks/FormItem';

export type SwitchProps = Pick<FormItemProps, 'label' | 'labelWeight'> & {
    name?: string;
    value: boolean;
    onChange?: (value: boolean) => void;
    helpElement?: ReactNode;
    required?: boolean;
    sideLabelPosition?: 'left' | 'right';
    sideLabel?: ReactNode;
    sideLabelColor?: 'default' | 'green' | 'red';
    /** Font size for side label. Defaults to 'sm' (14px) */
    sideLabelSize?: 'sm' | 'base';
    disabled?: boolean;
};

const Switch = ({
    label,
    value,
    onChange,
    helpElement,
    name,
    sideLabel,
    sideLabelPosition = 'right',
    sideLabelColor = 'default',
    sideLabelSize = 'sm',
    labelWeight,
    disabled,
    required,
}: SwitchProps) => {
    const labelColorClass =
        sideLabelColor === 'green' ? 'text-green-600' : sideLabelColor === 'red' ? 'text-red-600' : 'text-neutral-800';
    const sideLabelTextSize = sideLabelSize === 'base' ? 'text-base' : 'text-sm';

    const switchElement = (
        <HeadSwitch
            checked={value}
            className={clsx(
                `group relative inline-flex h-6 w-11 focus:ring-teal-600 data-[checked]:bg-teal-600`,
                `flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-neutral-300 transition-colors`,
                `duration-200 ease-in-out focus:ring-2 focus:ring-offset-2 focus:outline-none`,
                `data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50`
            )}
            data-cy="switchField"
            disabled={disabled}
            onChange={onChange}
        >
            <span className="sr-only">Use setting</span>
            <span
                aria-hidden="true"
                className={clsx(
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow',
                    'ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5'
                )}
            />
        </HeadSwitch>
    );

    return (
        <FormItem dataInputName={name} label={label} required={required} labelWeight={labelWeight}>
            <div className="flex items-center space-x-1">
                {sideLabelPosition === 'left' && (
                    <span className={clsx('font-medium', sideLabelTextSize, labelColorClass, 'mr-3')}>{sideLabel}</span>
                )}
                {switchElement}
                {sideLabelPosition === 'right' && (
                    <span className={clsx('font-medium', sideLabelTextSize, labelColorClass, 'ml-3')}>{sideLabel}</span>
                )}
            </div>
            {helpElement && <p className="mt-2 text-sm text-gray-500">{helpElement}</p>}
        </FormItem>
    );
};
export default Switch;
